import Seo from "@/components/Seo";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import BrandMark from "@/components/BrandMark";
import CollegeAutocomplete from "@/components/CollegeAutocomplete";
import { ChevronDown, ChevronUp, GraduationCap } from "lucide-react";

function PostgradBlock({ level, stateKey, form, setForm, testidPrefix }) {
  const [open, setOpen] = useState(false);
  const collegeField = `${stateKey}_college`;
  const yearField = `${stateKey}_batch_year`;
  return (
    <div className="rounded-xl border border-[color:var(--ayur-border)] bg-white/60 overflow-hidden">
      <button
        type="button"
        data-testid={`${testidPrefix}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="inline-flex items-center gap-2 text-sm font-medium text-[color:var(--ayur-ink)]">
          <GraduationCap className="w-4 h-4 text-[color:var(--ayur-emerald)]" />
          {level} <span className="text-[color:var(--ayur-mute)] font-normal">(optional)</span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-[color:var(--ayur-mute)]" /> : <ChevronDown className="w-4 h-4 text-[color:var(--ayur-mute)]" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-[color:var(--ayur-border)] pt-3">
          <div>
            <Label>College</Label>
            <CollegeAutocomplete
              value={form[collegeField]}
              onChange={(v) => setForm((f) => ({ ...f, [collegeField]: v }))}
              placeholder="e.g. IPGT&RA Jamnagar"
              testid={`${testidPrefix}-college-input`}
              authOptional
            />
          </div>
          <div>
            <Label>Batch year</Label>
            <Input
              data-testid={`${testidPrefix}-year-input`}
              type="number" min="1950" max="2035"
              value={form[yearField]}
              onChange={(e) => setForm((f) => ({ ...f, [yearField]: e.target.value }))}
              className="bg-white"
              placeholder="2022"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Signup() {
  const { register, meta } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", email: "", password: "",
    college: "", university: "", batch_year: "",
    degree: "BAMS", specialty: "", bio: "",
    md_college: "", md_batch_year: "",
    ms_college: "", ms_batch_year: "",
    phd_college: "", phd_batch_year: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.degree) { toast.error("Please select your degree"); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        email: form.email.trim().toLowerCase(),
        batch_year: parseInt(form.batch_year, 10),
        specialty: form.specialty || null,
        md_college: form.md_college?.trim() || null,
        md_batch_year: form.md_batch_year ? parseInt(form.md_batch_year, 10) : null,
        ms_college: form.ms_college?.trim() || null,
        ms_batch_year: form.ms_batch_year ? parseInt(form.ms_batch_year, 10) : null,
        phd_college: form.phd_college?.trim() || null,
        phd_batch_year: form.phd_batch_year ? parseInt(form.phd_batch_year, 10) : null,
      };
      await register(payload);
      toast.success("Welcome to VaidGram");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[color:var(--ayur-cream)]">
      <Seo title="Join free — create your Ayurveda scholar profile" description="Sign up for VaidGram with your BAMS college, university and batch year. Reconnect with batchmates, follow Gurus and find Ayurveda jobs." path="/signup" />
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1659353888906-adb3e0041693?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Contemporary Ayurveda practitioner"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[color:var(--ayur-emerald)]/60" />
        <div className="absolute inset-0 p-14 flex flex-col justify-between text-white">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center text-white"><BrandMark size={48} /></span>
            <span className="font-serif-display text-2xl">VaidGram</span>
          </div>
          <div>
            <div className="label-eyebrow text-[color:var(--ayur-gold)]">create profile</div>
            <h2 className="font-serif-display text-4xl mt-3 leading-tight max-w-md">
              Every scholar signs their college, university & batch — that’s the whole verification.
            </h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <form data-testid="signup-form" onSubmit={submit} className="w-full max-w-md space-y-4">
          <div>
            <div className="label-eyebrow">Join</div>
            <h1 className="font-serif-display text-3xl mt-2 text-[color:var(--ayur-emerald)]">Create your scholar profile</h1>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label>Full name</Label>
              <Input data-testid="signup-name-input" required value={form.full_name} onChange={set("full_name")} className="bg-white" placeholder="Dr. Ananya Iyer" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input data-testid="signup-email-input" required type="email" value={form.email} onChange={set("email")} className="bg-white" placeholder="you@ayur.edu" />
              </div>
              <div>
                <Label>Password</Label>
                <Input data-testid="signup-password-input" required type="password" minLength={6} value={form.password} onChange={set("password")} className="bg-white" placeholder="min 6 chars" />
              </div>
            </div>

            <div className="pt-1">
              <div className="label-eyebrow text-[color:var(--ayur-terracotta)]">Undergrad — BAMS</div>
            </div>
            <div>
              <Label>BAMS College *</Label>
              <CollegeAutocomplete
                value={form.college}
                onChange={(v) => setForm((f) => ({ ...f, college: v }))}
                placeholder="Government Ayurveda College, Pune"
                required
                testid="signup-college-input"
                authOptional
              />
            </div>
            <div>
              <Label>University *</Label>
              <Input data-testid="signup-university-input" required value={form.university} onChange={set("university")} className="bg-white" placeholder="Maharashtra University of Health Sciences" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>BAMS Batch Year *</Label>
                <Input data-testid="signup-batch-year-input" required type="number" min="1950" max="2035" value={form.batch_year} onChange={set("batch_year")} className="bg-white" placeholder="2018" />
              </div>
              <div>
                <Label>Current Degree *</Label>
                <Select value={form.degree} onValueChange={(v) => setForm((f) => ({ ...f, degree: v }))}>
                  <SelectTrigger data-testid="signup-degree-select" className="bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {meta.degrees.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="pt-3">
              <div className="label-eyebrow text-[color:var(--ayur-terracotta)]">Postgrad (if any)</div>
              <p className="text-xs text-[color:var(--ayur-mute)] mb-2">
                Add MD, MS or PhD if you did them at a different college. Skip if none.
              </p>
              <div className="space-y-2">
                <PostgradBlock level="MD (Ayurveda)" stateKey="md" form={form} setForm={setForm} testidPrefix="signup-postgrad-md" />
                <PostgradBlock level="MS (Ayurveda)" stateKey="ms" form={form} setForm={setForm} testidPrefix="signup-postgrad-ms" />
                <PostgradBlock level="PhD (Ayurveda)" stateKey="phd" form={form} setForm={setForm} testidPrefix="signup-postgrad-phd" />
              </div>
            </div>

            <div>
              <Label>Specialty (optional)</Label>
              <Select value={form.specialty} onValueChange={(v) => setForm((f) => ({ ...f, specialty: v }))}>
                <SelectTrigger data-testid="signup-specialty-select" className="bg-white"><SelectValue placeholder="Choose your primary specialty" /></SelectTrigger>
                <SelectContent>
                  {meta.specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Short bio (optional)</Label>
              <Textarea rows={3} value={form.bio} onChange={set("bio")} className="bg-white resize-none" placeholder="Areas of interest, current work, guru-parampara..." />
            </div>
          </div>

          <Button data-testid="signup-submit-button" type="submit" disabled={loading}
            className="w-full bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white py-6 rounded-full">
            {loading ? "Creating profile…" : "Create profile"}
          </Button>
          <p className="text-sm text-[color:var(--ayur-ink-2)]">
            Already a member? <Link to="/login" className="text-[color:var(--ayur-emerald)] font-semibold link-underline">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
